// Lazy .pk3 (zip) reader: only the central directory is parsed up front,
// individual files are sliced out of the Blob and inflated on demand. ET's
// pak0.pk3 is hundreds of MB, so unzipping everything is not an option.

import { inflateSync } from 'fflate';

interface ZipEntry {
  /** Original-case path inside the archive. */
  name: string;
  method: number;
  compressedSize: number;
  size: number;
  localHeaderOffset: number;
}

const EOCD_SIG = 0x06054b50;
const CDIR_SIG = 0x02014b50;
const LOCAL_SIG = 0x04034b50;

async function readRange(blob: Blob, start: number, end: number): Promise<DataView> {
  const buf = await blob.slice(start, end).arrayBuffer();
  return new DataView(buf);
}

function decodeName(bytes: Uint8Array, utf8: boolean): string {
  return new TextDecoder(utf8 ? 'utf-8' : 'latin1').decode(bytes);
}

export class Pk3Archive {
  /** Lower-case path -> entry. */
  readonly entries = new Map<string, ZipEntry>();

  private constructor(
    readonly name: string,
    private readonly blob: Blob,
  ) {}

  static async open(blob: Blob, name: string): Promise<Pk3Archive> {
    const archive = new Pk3Archive(name, blob);
    await archive.readCentralDirectory();
    return archive;
  }

  private async readCentralDirectory(): Promise<void> {
    const size = this.blob.size;
    const tailLen = Math.min(size, 65535 + 22);
    const tail = await readRange(this.blob, size - tailLen, size);
    let eocd = -1;
    for (let i = tailLen - 22; i >= 0; i--) {
      if (tail.getUint32(i, true) === EOCD_SIG) {
        eocd = i;
        break;
      }
    }
    if (eocd < 0) throw new Error(`${this.name}: not a zip/pk3 file`);
    const count = tail.getUint16(eocd + 10, true);
    const cdSize = tail.getUint32(eocd + 12, true);
    const cdOffset = tail.getUint32(eocd + 16, true);
    const cd = await readRange(this.blob, cdOffset, cdOffset + cdSize);
    const bytes = new Uint8Array(cd.buffer);

    let p = 0;
    for (let i = 0; i < count; i++) {
      if (cd.getUint32(p, true) !== CDIR_SIG) throw new Error(`${this.name}: corrupt central directory`);
      const flags = cd.getUint16(p + 8, true);
      const method = cd.getUint16(p + 10, true);
      const compressedSize = cd.getUint32(p + 20, true);
      const fileSize = cd.getUint32(p + 24, true);
      const nameLen = cd.getUint16(p + 28, true);
      const extraLen = cd.getUint16(p + 30, true);
      const commentLen = cd.getUint16(p + 32, true);
      const localHeaderOffset = cd.getUint32(p + 42, true);
      const name = decodeName(bytes.subarray(p + 46, p + 46 + nameLen), (flags & 0x800) !== 0).replace(/\\/g, '/');
      p += 46 + nameLen + extraLen + commentLen;
      if (name.endsWith('/')) continue; // directory
      this.entries.set(name.toLowerCase(), { name, method, compressedSize, size: fileSize, localHeaderOffset });
    }
  }

  /** The whole pk3 (to hand it to a worker). */
  get file(): Blob {
    return this.blob;
  }

  has(path: string): boolean {
    return this.entries.has(path.toLowerCase());
  }

  async read(path: string): Promise<Uint8Array> {
    const entry = this.entries.get(path.toLowerCase());
    if (!entry) throw new Error(`${this.name}: ${path} not found`);
    const header = await readRange(this.blob, entry.localHeaderOffset, entry.localHeaderOffset + 30);
    if (header.getUint32(0, true) !== LOCAL_SIG) throw new Error(`${this.name}: bad local header for ${path}`);
    const dataStart = entry.localHeaderOffset + 30 + header.getUint16(26, true) + header.getUint16(28, true);
    const raw = new Uint8Array(await this.blob.slice(dataStart, dataStart + entry.compressedSize).arrayBuffer());
    if (entry.method === 0) return raw;
    if (entry.method === 8) return inflateSync(raw, { out: new Uint8Array(entry.size) });
    throw new Error(`${this.name}: unsupported compression method ${entry.method} for ${path}`);
  }
}
